import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Plus, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { configuracoesFinanceiroService, ConfiguracoesFinanceiro as IConfiguracoesFinanceiro, DEFAULT_CONFIG } from '@/services/configuracoesFinanceiroService';

function ArrayEditor({ 
  items, 
  setItems, 
  title, 
  description,
  placeholder = 'Adicionar novo item'
}: { 
  items: string[], 
  setItems: (items: string[]) => void, 
  title: string,
  description: string,
  placeholder?: string
}) {
  const [newItem, setNewItem] = useState('');

  const handleAdd = () => {
    if (!newItem.trim()) return;
    if (items.includes(newItem.trim())) {
      toast.warning('Este item já existe na lista');
      return;
    }
    setItems([...items, newItem.trim()]);
    setNewItem('');
  };

  const handleRemove = (index: number) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-semibold">{title}</h4>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <div className="flex gap-2">
        <Input 
          value={newItem} 
          onChange={(e) => setNewItem(e.target.value)} 
          placeholder={placeholder}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
        />
        <Button variant="outline" onClick={handleAdd} type="button">
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      <div className="space-y-2 mt-2">
        {items.map((item, idx) => (
          <div key={idx} className="flex items-center justify-between p-2 border rounded-md bg-muted/50">
            <span className="text-sm">{item}</span>
            <Button variant="ghost" size="sm" onClick={() => handleRemove(idx)} className="text-destructive hover:text-destructive h-8 w-8 p-0">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        {items.length === 0 && (
          <p className="text-xs text-muted-foreground italic p-2">Nenhum item configurado.</p>
        )}
      </div>
    </div>
  );
}

function configEquals(a: IConfiguracoesFinanceiro, b: IConfiguracoesFinanceiro): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function ConfiguracoesFinanceiro() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<IConfiguracoesFinanceiro>(DEFAULT_CONFIG);
  const [configSalvo, setConfigSalvo] = useState<IConfiguracoesFinanceiro | null>(null);

  const hasChanges = configSalvo !== null && !configEquals(config, configSalvo);

  useEffect(() => {
    carregarConfiguracoes();
  }, []);

  const carregarConfiguracoes = async () => {
    try {
      setLoading(true);
      const data = await configuracoesFinanceiroService.getConfig();
      setConfig(data);
      setConfigSalvo(data);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      // Fallback to default config if there's an error
      setConfig(DEFAULT_CONFIG);
      setConfigSalvo(DEFAULT_CONFIG);
      toast.error('Erro ao carregar configurações, usando padrões');
    } finally {
      setLoading(false);
    }
  };

  const salvarConfiguracoes = async () => {
    try {
      setSaving(true);
      await configuracoesFinanceiroService.saveConfig(config);
      setConfigSalvo(config);
      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      toast.error('Erro ao salvar configurações no servidor, mas foram salvas localmente.');
    } finally {
      setSaving(false);
    }
  };

  const updateContasPagar = (field: keyof IConfiguracoesFinanceiro['contasPagar'], value: string[]) => {
    setConfig(prev => ({
      ...prev,
      contasPagar: {
        ...prev.contasPagar,
        [field]: value
      }
    }));
  };

  const updateContasReceber = (field: keyof IConfiguracoesFinanceiro['contasReceber'], value: string[]) => {
    setConfig(prev => ({
      ...prev,
      contasReceber: {
        ...prev.contasReceber,
        [field]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Configurações do Financeiro</CardTitle>
          <CardDescription>
            Personalize as opções disponíveis nos formulários financeiros
          </CardDescription>
        </div>
        <Button onClick={salvarConfiguracoes} disabled={!hasChanges || saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Salvar Alterações
        </Button>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="pagar" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="pagar">Contas a Pagar</TabsTrigger>
            <TabsTrigger value="receber">Contas a Receber</TabsTrigger>
          </TabsList>
          
          <TabsContent value="pagar" className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <ArrayEditor 
                title="Categorias"
                description="Categorias disponíveis para classificar contas a pagar"
                items={config.contasPagar.categorias}
                setItems={(items) => updateContasPagar('categorias', items)}
              />
              <ArrayEditor 
                title="Tipos de Pagamento"
                description="Opções de tipo (ex: À vista, Parcelado)"
                items={config.contasPagar.tiposPagamento}
                setItems={(items) => updateContasPagar('tiposPagamento', items)}
              />
              <ArrayEditor 
                title="Formas de Pagamento"
                description="Meios de pagamento (ex: PIX, Boleto)"
                items={config.contasPagar.formasPagamento}
                setItems={(items) => updateContasPagar('formasPagamento', items)}
              />
            </div>
          </TabsContent>

          <TabsContent value="receber" className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <ArrayEditor 
                title="Categorias"
                description="Categorias disponíveis para classificar contas a receber"
                items={config.contasReceber.categorias}
                setItems={(items) => updateContasReceber('categorias', items)}
              />
              <ArrayEditor 
                title="Tipos de Pagamento"
                description="Opções de tipo (ex: À vista, Parcelado)"
                items={config.contasReceber.tiposPagamento}
                setItems={(items) => updateContasReceber('tiposPagamento', items)}
              />
              <ArrayEditor 
                title="Formas de Pagamento"
                description="Meios de pagamento (ex: PIX, Boleto)"
                items={config.contasReceber.formasPagamento}
                setItems={(items) => updateContasReceber('formasPagamento', items)}
              />
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
